import { Box, IconButton, Typography } from '@mui/material'
import React from 'react'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { useHideDirtyState } from '../advanced-settings/providers'
import { DirtyMark, TrashIcon } from '../icons'

interface FormSectionTitleProps {
  name: string
  onClick?: () => void
  header?: 'h2' | 'h3'
  isDirty?: boolean
  isError?: boolean
  rightContent?: React.ReactNode
}

/**
 * Title/Header for a form section (device sub-forms like heatpump/meters/evse/io, and settings sections like location/tariffs/storage).
 * Renders the section name with an optional dirty mark, an error color when the section is invalid,
 * and an optional delete button. The header can be an h2 or an h3. It is h2 by default.
 */
export const FormSectionTitle: React.FC<FormSectionTitleProps> = ({ name, onClick, header = 'h2', isDirty, isError, rightContent }) => {
  const hideDirtyState = useHideDirtyState()
  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Typography
        component={header}
        variant="h3"
        display={'inline'}
        sx={theme => ({
          color: isError ? theme.palette.error.main : undefined,
          position: 'relative',
          transition: theme.transitions.create('color', { duration: theme.transitions.duration.standard }),
        })}
      >
        {name}
        <Box
          sx={{
            position: 'absolute',
            top: '-40%',
            left: '100%',
            scale: 0.7,
            opacity: isDirty && !hideDirtyState ? 1 : 0,
            transition: theme => theme.transitions.create('opacity', { duration: theme.transitions.duration.standard }),
          }}
        >
          <DirtyMark />
        </Box>
      </Typography>

      {canEdit && onClick && (
        <IconButton size="small" onClick={onClick} sx={{ ml: 2 }} aria-label="remove device">
          <TrashIcon />
        </IconButton>
      )}

      {rightContent}
    </Box>
  )
}
